import React, { useContext, useState, useEffect } from 'react';
import { ReactReduxContext } from 'react-redux';
import hoistNonReactStatics from 'hoist-non-react-statics';
import wrapDisplayName from 'recompose/wrapDisplayName';
import { createDynamicTarget } from '@redux-dynostore/core';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
var isObject = function isObject(value) {
  var type = typeof value;
  return type == "object" && value != null && !Array.isArray(value);
};

var splitOptions = function splitOptions(args) {
  var _args$slice = args.slice(-1),
      lastItem = _args$slice[0];

  return isObject(lastItem) ? [args.slice(0, -1), lastItem] : [args, {}];
};

var DynamicContext = /*#__PURE__*/React.createContext(null); // eslint-disable-next-line react/prop-types

var DynamicProvider = function DynamicProvider(_ref) {
  var children = _ref.children;
  var inheritedFirstRender = useContext(DynamicContext);

  var _useState = useState(inheritedFirstRender === null),
      firstRender = _useState[0],
      setFirstRender = _useState[1];

  useEffect(function () {
    setFirstRender(false);
  }, []);
  return /*#__PURE__*/React.createElement(DynamicContext.Provider, {
    value: firstRender
  }, children);
};

var createDynamic = function createDynamic(identifier, enhancers, options) {
  var _options$context = options.context,
      context = _options$context === void 0 ? ReactReduxContext : _options$context;
  var dynamicEnhancer = createDynamicTarget(enhancers)(identifier);
  return function (Component) {
    var Dynamic = /*#__PURE__*/React.forwardRef(function (props, ref) {
      var _useContext = useContext(context),
          store = _useContext.store;

      var _useState2 = useState(store),
          lastStore = _useState2[0],
          setLastStore = _useState2[1];

      var firstRender = useContext(DynamicContext);

      var _useState3 = useState(function () {
        return firstRender && dynamicEnhancer(store)(Component);
      }),
          EnhancedComponent = _useState3[0],
          setEnhancedComponent = _useState3[1];

      useEffect(function () {
        if (!EnhancedComponent || store !== lastStore) {
          setEnhancedComponent(function () {
            return dynamicEnhancer(store)(Component);
          });
          setLastStore(store);
        }
      }, [store]);
      return EnhancedComponent && /*#__PURE__*/React.createElement(EnhancedComponent, _extends({
        identifier: identifier
      }, props, {
        ref: ref
      }));
    });
    hoistNonReactStatics(Dynamic, Component);
    Dynamic.displayName = wrapDisplayName(Component, 'Dynamic');

    Dynamic.createInstance = function (instanceIdentifier) {
      for (var _len = arguments.length, instanceEnhancers = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        instanceEnhancers[_key - 1] = arguments[_key];
      }

      return createDynamic(instanceIdentifier, enhancers.concat(instanceEnhancers), options)(Component);
    };

    return Dynamic;
  };
};

var dynamic = function dynamic(identifier) {
  for (var _len2 = arguments.length, enhancers = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
    enhancers[_key2 - 1] = arguments[_key2];
  }

  return createDynamic.apply(void 0, [identifier].concat(splitOptions(enhancers)));
};

export default dynamic;
export { DynamicProvider };
